/**
 * The blanks a template leaves to fill (`[[petitioner_name]]`, `[Insert job
 * title]`, `[_____]`), read off the document after each rendering. Folded to
 * one summary row; open, each row says where the agents take the value from,
 * and a click finds it in the open Word document. A template with no blanks
 * gets `EmptyTemplateCallout` instead.
 */
import { Braces, ChevronDown, ChevronRight, Search } from "lucide-react";
import { useState, type FC } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import type { FirmLibraryCategory, TemplateField } from "../types";
import { EntryCallout } from "./EntryCallout";

type Props = {
  fields: TemplateField[] | null | undefined;
  /** Find a field in the open document; absent when no document is open. */
  onReveal?: (token: string) => boolean;
};

export const EmptyTemplateCallout: FC<{
  fields: TemplateField[] | null | undefined;
  category: FirmLibraryCategory;
}> = ({ fields, category }) =>
  fields && fields.length === 0 && category === "template" ? (
    <EntryCallout
      tone="info"
      icon={Braces}
      title="This template has no blanks"
      data-testid="template-fields-empty"
    >
      <p>
        Mark what changes per case with double brackets, e.g.{" "}
        <code className="font-mono">[[beneficiary_name]]</code>, and the agents
        fill them from the case.
      </p>
    </EntryCallout>
  ) : null;

export const TemplateFieldsPanel: FC<Props> = ({ fields, onReveal }) => {
  const [open, setOpen] = useState(false);
  const [missing, setMissing] = useState<string | null>(null);
  if (!fields?.length) return null;

  const fromCase = fields.filter((field) => field.source).length;
  const sources =
    fromCase === fields.length
      ? "all come from the case"
      : `${fromCase} from the case, ${fields.length - fromCase} by the drafter`;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-lg ring-1 ring-line-2"
      data-testid="template-fields"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-base hover:bg-fill-1"
          data-testid="template-fields-toggle"
        >
          <Braces className="size-3.5 shrink-0 text-fg-3" />
          <span className="font-medium text-fg">Fields to Fill</span>
          <span className="text-fg-3">· {fields.length}</span>
          <span className="min-w-0 flex-1 truncate text-fg-3">· {sources}</span>
          <Chevron className="size-3.5 shrink-0 text-fg-3" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="divide-y divide-line-1 border-t border-line-1">
          {fields.map((field) => (
            <li key={field.key}>
              <button
                type="button"
                disabled={!onReveal}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-base",
                  onReveal && "hover:bg-fill-1"
                )}
                onClick={() => {
                  if (!onReveal) return;
                  setMissing(onReveal(field.token) ? null : field.key);
                }}
                title={onReveal ? "Find it in the document" : undefined}
                data-testid={`template-field-${field.key}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{field.label}</span>
                  <span className="block truncate font-mono text-sm text-fg-3">
                    {field.token}
                    {field.count > 1 ? ` · ${field.count} places` : ""}
                  </span>
                </span>
                <StatusBadge tone={field.source ? "success" : "neutral"}>
                  {field.source ?? "Filled by Hand"}
                </StatusBadge>
                {onReveal ? (
                  <Search className="size-3.5 shrink-0 text-fg-3" />
                ) : null}
              </button>
              {missing === field.key ? (
                <p className="px-3 pb-1.5 text-sm text-warn">
                  Not found in the open document. It may still be loading.
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};
